// src/pages/Reports.jsx
// forReport → { data: [{id, name, date_entered, invoiced, withtax, subtotal, tax_amount, company, rep}] }
// getLeadReport → { data: [...] }
// getLocationReport → { locations: [...], reps: [...] }
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Reports as API, Leads } from '../services/api';

const today   = new Date().toISOString().slice(0, 10);
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function Reports() {
  const [from, setFrom] = useState(firstOfMonth);
  const [to,   setTo]   = useState(today);
  const [data, setData] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab,  setTab]  = useState('sales');   // 'sales' | 'leads' | 'location' | 'daily'
  const [leads, setLeads] = useState([]);

  // ── Location report state ─────────────────────────────────
  const [locData, setLocData]     = useState({ locations: [], reps: [] });
  const [locBusy, setLocBusy]     = useState(false);
  const [repLocFilter, setRepLocFilter] = useState('all');
  const [repSort, setRepSort]     = useState('revenue'); // 'revenue' | 'quotes' | 'win_rate'

  // ── Daily status summary state ────────────────────────────
  const [dailyDate, setDailyDate] = useState(today);
  const [dailyData, setDailyData] = useState({ stages: [], unassigned_count: 0 });
  const [dailyBusy, setDailyBusy] = useState(false);
  const [drillDown, setDrillDown] = useState(null); // { label, qstatus } or null
  const [drillList, setDrillList] = useState([]);
  const [drillBusy, setDrillBusy] = useState(false);

  const runSales = useCallback(() => {
    setBusy(true);
    API.sales(from, to).then(setData).catch(console.error).finally(() => setBusy(false));
  }, [from, to]);

  const runLeads = useCallback(() => {
    setBusy(true);
    Leads.report().then(setLeads).catch(console.error).finally(() => setBusy(false));
  }, []);

  const runLocation = useCallback(() => {
    setLocBusy(true);
    API.byLocation(from, to)
      .then(r => setLocData({ locations: r?.locations || [], reps: r?.reps || [] }))
      .catch(console.error)
      .finally(() => setLocBusy(false));
  }, [from, to]);

  const runDaily = useCallback(() => {
    setDailyBusy(true);
    API.dailyStatus(dailyDate)
      .then(r => setDailyData({ stages: r?.stages || [], unassigned_count: r?.unassigned_count || 0 }))
      .catch(console.error)
      .finally(() => setDailyBusy(false));
  }, [dailyDate]);

  const openDrillDown = (label, qstatus) => {
    setDrillDown({ label, qstatus });
    setDrillBusy(true);
    API.quotesByStatus(dailyDate, qstatus)
      .then(setDrillList)
      .catch(console.error)
      .finally(() => setDrillBusy(false));
  };

  useEffect(() => {
    if (tab === 'sales') runSales();
    else if (tab === 'leads') runLeads();
    else if (tab === 'location') runLocation();
    else runDaily();
  }, [tab]);

  // ── Derived stats (sales tab) ────────────────────────────
  const totalRev  = data.reduce((s, r) => s + parseFloat(r.subtotal  || 0), 0);
  const totalTax  = data.reduce((s, r) => s + parseFloat(r.tax_amount|| 0), 0);
  const totalInv  = data.filter(r => r.invoiced >= 1).length;
  const totalQt   = data.filter(r => r.invoiced == 0).length;

  const byDay = data.reduce((acc, r) => {
    const d = r.date_entered?.slice(0, 10) || 'unknown';
    acc[d]  = (acc[d] || 0) + parseFloat(r.subtotal || 0);
    return acc;
  }, {});
  const barData = Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, amount]) => ({ date: date.slice(5), amount: +amount.toFixed(0) }));

  // ── Lead status distribution ──────────────────────────────
  const STAT_LABEL = { '1':'Active','2':'Follow Up','3':'Closed','4':'Lost','7':'Quoted','8':'Invoiced' };
  const leadByStatus = leads.reduce((acc, l) => {
    const k = STAT_LABEL[l.lstatus] || l.lstatus;
    acc[k]  = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const leadBarData = Object.entries(leadByStatus).map(([name, count]) => ({ name, count }));

  // ── Location tab derived views ────────────────────────────
  const locChartData = locData.locations.map(l => ({ name: l.location_name, revenue: l.revenue }));

  const filteredReps = locData.reps
    .filter(r => repLocFilter === 'all' || String(r.location_id) === repLocFilter)
    .slice()
    .sort((a, b) => b[repSort] - a[repSort]);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📈 Reports</div>
      </div>

      {/* Tab switcher */}
      <div className="tabs" style={{ marginBottom: '1.2rem' }}>
        <button className={`tab${tab === 'sales' ? ' active' : ''}`} onClick={() => setTab('sales')}>Sales Report</button>
        <button className={`tab${tab === 'leads' ? ' active' : ''}`} onClick={() => setTab('leads')}>Lead Report</button>
        <button className={`tab${tab === 'location' ? ' active' : ''}`} onClick={() => setTab('location')}>By Location</button>
        <button className={`tab${tab === 'daily' ? ' active' : ''}`} onClick={() => setTab('daily')}>Daily Summary</button>
      </div>

      {/* Shared date range — used by Sales and Location tabs */}
      {(tab === 'sales' || tab === 'location') && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1.2rem' }}>
          <label style={{ width: 160 }}>From<input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label style={{ width: 160 }}>To  <input type="date" value={to}   onChange={e => setTo(e.target.value)} /></label>
          <button className="btn btn-primary" onClick={tab === 'sales' ? runSales : runLocation} disabled={tab === 'sales' ? busy : locBusy}>
            {(tab === 'sales' ? busy : locBusy) ? '…' : 'Run'}
          </button>
        </div>
      )}

      {/* Date picker for Daily Summary */}
      {tab === 'daily' && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1.2rem' }}>
          <label style={{ width: 180 }}>Date<input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)} max={today} /></label>
          <button className="btn btn-primary" onClick={runDaily} disabled={dailyBusy}>{dailyBusy ? '…' : 'Run'}</button>
          <button className="btn btn-ghost" onClick={() => setDailyDate(today)}>Today</button>
        </div>
      )}

      {/* ── SALES REPORT ── */}
      {tab === 'sales' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: '1.2rem' }}>
            {[
              { icon: '💵', label: 'Total Revenue',  val: `₹${totalRev.toLocaleString()}`, color: 'var(--green)' },
              { icon: '🧾', label: 'GST Collected',  val: `₹${totalTax.toLocaleString()}`, color: 'var(--yellow)' },
              { icon: '📋', label: 'Quotes',          val: totalQt,                         color: 'var(--accent)' },
              { icon: '✅', label: 'Invoices',        val: totalInv,                        color: 'var(--purple)' },
            ].map(k => (
              <div key={k.label} className="kpi-card" style={{ borderTopColor: k.color }}>
                <div style={{ fontSize: '1.3rem' }}>{k.icon}</div>
                <div className="kpi-val" style={{ color: k.color, fontSize: '1.5rem' }}>{k.val}</div>
                <div className="kpi-lbl">{k.label}</div>
              </div>
            ))}
          </div>

          {barData.length > 0 && (
            <div className="card" style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '1rem' }}>Revenue by Day (₹)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
                    formatter={v => [`₹${v.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="amount" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
            data.length === 0 ? <div className="empty">No data for this range</div> :
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th><th>Quote / Invoice</th><th>Customer</th>
                    <th>Rep</th><th>Subtotal</th><th>GST</th><th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(r => (
                    <tr key={r.id}>
                      <td>{r.date_entered?.slice(0, 10)}</td>
                      <td className="fw-bold">{r.name || `#${r.id}`}</td>
                      <td>{r.company || r.customer}</td>
                      <td>{r.rep || r.user_id}</td>
                      <td>₹{parseFloat(r.subtotal  || 0).toLocaleString()}</td>
                      <td>₹{parseFloat(r.tax_amount|| 0).toLocaleString()}</td>
                      <td>
                        <span className="badge" style={{ background: r.invoiced >= 1 ? 'var(--green)' : 'var(--faint)', color: '#0f172a', fontSize: '.7rem' }}>
                          {r.invoiced >= 1 ? 'Invoice' : 'Quote'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </>
      )}

      {/* ── LEAD REPORT ── */}
      {tab === 'leads' && (
        <>
          <button className="btn btn-primary btn-sm" onClick={runLeads} disabled={busy} style={{ marginBottom: '1.2rem' }}>
            {busy ? '…' : 'Refresh'}
          </button>

          {leadBarData.length > 0 && (
            <div className="card" style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '1rem' }}>Leads by Status</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={leadBarData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="var(--green)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
            leads.length === 0 ? <div className="empty">No lead data</div> :
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Company</th><th>Contact</th><th>Status</th><th>Last Update</th></tr></thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id}>
                      <td className="fw-bold">{l.company}</td>
                      <td>{l.name}</td>
                      <td>
                        <span className="badge" style={{ background: 'var(--surface)', fontSize: '.72rem' }}>
                          {STAT_LABEL[l.lstatus] || l.lstatus}
                        </span>
                      </td>
                      <td>{l.lstat_dt || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </>
      )}

      {/* ── BY LOCATION ── */}
      {tab === 'location' && (
        locBusy ? <div className="spinner-wrap"><div className="spinner" /></div> : (
          <>
            {locData.locations.length === 0 ? (
              <div className="empty">No quote data for this range</div>
            ) : (
              <>
                <div className="card" style={{ marginBottom: '1.2rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '1rem' }}>Revenue by Location (₹)</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={locChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="name" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
                        formatter={v => [`₹${v.toLocaleString()}`, 'Revenue']}
                      />
                      <Bar dataKey="revenue" fill="var(--purple)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="tbl-wrap" style={{ marginBottom: '1.6rem' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Location</th><th>Reps</th><th>Quotes</th>
                        <th>Revenue</th><th>Invoiced</th><th>Win Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locData.locations.map(l => (
                        <tr key={l.location_id}>
                          <td className="fw-bold">{l.location_name}</td>
                          <td>{l.rep_count}</td>
                          <td>{l.quotes}</td>
                          <td>₹{l.revenue.toLocaleString()}</td>
                          <td>{l.invoiced}</td>
                          <td>
                            <span className="badge" style={{
                              background: l.win_rate >= 50 ? 'var(--green)' : 'var(--yellow)',
                              color: '#0f172a', fontSize: '.72rem',
                            }}>
                              {l.win_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Rep leaderboard */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.8rem', flexWrap: 'wrap', gap: '.6rem' }}>
              <div style={{ fontWeight: 600 }}>Rep Leaderboard</div>
              <div style={{ display: 'flex', gap: '.6rem' }}>
                <select style={{ width: 'auto' }} value={repLocFilter} onChange={e => setRepLocFilter(e.target.value)}>
                  <option value="all">All Locations</option>
                  {locData.locations.filter(l => l.location_id !== 'unassigned').map(l => (
                    <option key={l.location_id} value={l.location_id}>{l.location_name}</option>
                  ))}
                </select>
                <select style={{ width: 'auto' }} value={repSort} onChange={e => setRepSort(e.target.value)}>
                  <option value="revenue">Sort: Revenue</option>
                  <option value="quotes">Sort: Quotes</option>
                  <option value="win_rate">Sort: Win Rate</option>
                </select>
              </div>
            </div>

            {filteredReps.length === 0 ? (
              <div className="empty">No rep activity for this range</div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>#</th><th>Rep</th><th>Location</th>
                      <th>Quotes</th><th>Revenue</th><th>Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReps.map((r, i) => (
                      <tr key={r.user_id}>
                        <td style={{ color: 'var(--muted)' }}>{i + 1}</td>
                        <td className="fw-bold">{r.name}</td>
                        <td>{r.location_name || '—'}</td>
                        <td>{r.quotes}</td>
                        <td>₹{r.revenue.toLocaleString()}</td>
                        <td>
                          <span className="badge" style={{
                            background: r.win_rate >= 50 ? 'var(--green)' : 'var(--yellow)',
                            color: '#0f172a', fontSize: '.72rem',
                          }}>
                            {r.win_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      {/* ── DAILY SUMMARY ── */}
      {tab === 'daily' && (
        dailyBusy ? <div className="spinner-wrap"><div className="spinner" /></div> : (
          <>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.8rem', marginBottom: '1.6rem' }}>
              {dailyData.stages.map(s => (
                <div
                  key={s.id}
                  className="card"
                  style={{ cursor: 'pointer', borderTop: `3px solid ${s.color}` }}
                  onClick={() => openDrillDown(s.label, s.id)}
                >
                  <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: '.3rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: s.color }}>{s.count}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--faint)', textTransform: 'capitalize' }}>{s.category}</div>
                </div>
              ))}
              {dailyData.unassigned_count > 0 && (
                <div
                  className="card"
                  style={{ cursor: 'pointer', borderTop: '3px solid var(--faint)' }}
                  onClick={() => openDrillDown('No Stage Set', null)}
                >
                  <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: '.3rem' }}>No Stage Set</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--faint)' }}>{dailyData.unassigned_count}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--faint)' }}>needs review</div>
                </div>
              )}
            </div>

            {dailyData.stages.every(s => s.count === 0) && dailyData.unassigned_count === 0 && (
              <div className="empty">No quotes created on {dailyDate}</div>
            )}

            <p style={{ color: 'var(--faint)', fontSize: '.8rem' }}>Click any card to see the quotes behind that number.</p>
          </>
        )
      )}

      {/* ── Drill-down modal ── */}
      {drillDown && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDrillDown(null)}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span className="modal-title">{drillDown.label} — {dailyDate}</span>
              <button className="btn-icon" onClick={() => setDrillDown(null)}>✕</button>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {drillBusy ? <div className="spinner-wrap"><div className="spinner" /></div> :
                drillList.length === 0 ? <div className="empty">No quotes in this stage</div> :
                drillList.map(q => (
                  <div key={q.id} className="list-row" style={{ marginBottom: '.5rem' }}>
                    <div className="row-main">
                      <div className="row-title">{q.name || `Quote #${q.id}`}</div>
                      <div className="row-sub">
                        {q.customer_name} · {q.rep_name}
                        {q.pono ? ` · PO ${q.pono}` : ''}
                      </div>
                    </div>
                    <div className="row-actions">
                      <span style={{ fontWeight: 700, color: 'var(--accent)' }}>₹{q.total.toLocaleString()}</span>
                    </div>
                  </div>
                ))
              }
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDrillDown(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
